// PANEL A′ — "OPEN NOW". The polls that are RUNNING on this sitting, and the one act
// an Administrator has on a running poll: giving it more time.
//
// ── IT EXISTS BECAUSE THE ACT HAD NO SCREEN ────────────────────────────────
// `POST /api/setup/ballot/:ballotId/extend` shipped complete and tested and was
// reachable only by curl. A registrar standing in a hall at the moment Synod asks for
// another twenty minutes cannot run curl, so the route was, in the only sense that
// matters, absent. This panel is the whole of its user interface.
//
// ── FIRST IN THE STACK, AND ABSENT WHEN EMPTY ──────────────────────────────
// It sits ABOVE the roll and the ballots because a poll that is open now is the most
// time-critical thing on the screen — the roll is frozen, the ballots are built, and
// the only clock still running is this one. And it renders NOTHING AT ALL when no poll
// is open: an empty panel headed "Open now" is furniture that trains an operator to
// scroll past the place the urgent thing will appear.
//
// ── NOT THE SETTINGS DRAWER, AND NOT A SETTING ─────────────────────────────
// Extending is a per-ballot LIFECYCLE act, the same shape as Open and Abandon, which
// already live as row acts in `BallotsPanel`. The estate's inline-card ban (SET-3)
// governs account, security, feature, integration and operational controls — the gear
// — and nothing here is one of those: this changes one ballot, not the portal.
//
// ── THREE ROW VARIANTS, DECIDED FROM THE ROW'S OWN WINDOW ──────────────────
// The state word says the poll is open; the SCHEDULE says what may be done to it, and
// the two are kept apart (`state.ts` owns the first, {@link pollWindow} the second):
//
//  1. BOUNDED AND RUNNING  — the close is in the future. The control reads "Give this
//     poll more time".
//  2. UNBOUNDED            — the row carries no closing time. NO CONTROL AT ALL. The
//     worker refuses this one `no_close_to_extend`, because putting a close on an
//     unbounded poll does not widen it, it NARROWS it, and shuts out every member who
//     had not yet reached the door. That is designed out of reach here rather than
//     met as a refusal after the press: an act that can never succeed should not be
//     offered, and the row says plainly why there is nothing to move.
//  3. THE CLOSE HAS PASSED — the poll's clock has run out while it is still 'open'
//     (SO 15.5: the room broke for lunch). The warn stripe, the plain consequence
//     ("Members cannot mark a paper until it is moved later"), and the control
//     reworded "Reopen the time on this poll", because "more time" is a lie about a
//     poll nobody can currently vote in.
//
// ── COLOUR CARRIES NO VERDICT ──────────────────────────────────────────────
// No danger colour anywhere: extending ENFRANCHISES — it lets more people vote — and
// red would editorialise an act the standing orders provide for. setup.css rule 2
// bans a success or danger tint on this screen. The one tint used is --color-warn on
// variant 3, and it is ATTENTION rather than a verdict (result.css rule 2 settles that
// reading): the fact it draws the eye to is that the poll's clock has run out, which
// is a fact about the clock and not a judgement on the poll. Every word and figure
// inside the stripe stays --color-ink.

import type { JSX } from 'react';
import { KIND_LABEL, STATE_LABEL, stateDotIsHollow } from './BallotsPanel';
import { RecordedBound, utcInWords } from './SchedulePanel';
import { permittedBallotActs } from './state';
import type { DraftBallot } from '../api/client';

/**
 * What this poll's CLOSING TIME lets an operator do — the row variant, and nothing
 * about the ballot's state.
 *
 * A BOUNDED poll's `nz` may still be `null`, and that is the pre-0015 row rather than
 * a hypothetical: `closes_at_nz` could hold no value on this engine until migration
 * 0015 replaced the CHECK it failed, so a ballot scheduled before then carries an
 * instant with no wall clock beside it. It is still a bounded, extendable poll — the
 * INSTANT is what `withinWindow` enforces — so the act stays offered and only the
 * rendering changes.
 */
type PollWindow =
  | { kind: 'unbounded'; reported: boolean }
  | { kind: 'running'; nz: string | null; utc: string }
  | { kind: 'passed'; nz: string | null; utc: string };

/**
 * Decide the variant from the row's recorded window.
 *
 * **KEYED ON `closesAtUtc`, NEVER ON `schedule.mode`** — the same choice the worker's
 * own gate 3 makes and for the same reason: a 'scheduled' ballot with both bounds null
 * is lawful and reachable from the operator's own screen, so the mode word would wave
 * through exactly the poll the unbounded arm exists for. The instant is also the
 * authority the poll actually runs on (`src/cast.ts`'s `withinWindow` enforces it),
 * which is why the wall clock's absence changes the rendering and not the answer.
 *
 * **COMPARING `closesAtUtc` TO `now` IS NOT THE TIMEZONE ARITHMETIC `SchedulePanel`
 * FORBIDS.** That rule is about a WALL CLOCK, which has no instant behind it until the
 * worker derives one; this is two INSTANTS, and an instant comparison is the same
 * answer in every zone on earth. Nothing here reads the browser's zone or constructs a
 * Date from anything the operator typed.
 *
 * An UNREADABLE instant reads as 'running', which is the safe direction: the act stays
 * offered, and the worker — which is the authority — refuses it `not_an_extension`
 * with the figure the repair needs. Withholding the control would strand a poll this
 * screen merely could not parse.
 *
 * `reported` distinguishes a poll that genuinely has no close from one whose window
 * this server did not send at all (deploy skew — `schedule` is optional for that alone),
 * so the row can say which without claiming the stronger fact.
 */
function pollWindow(ballot: DraftBallot, nowMs: number): PollWindow {
  const schedule = ballot.schedule;
  const utc = schedule?.closesAtUtc ?? null;
  if (utc === null) return { kind: 'unbounded', reported: schedule !== undefined };
  const nz = schedule?.closesAtNz ?? null;
  const closeMs = Date.parse(utc);
  if (!Number.isFinite(closeMs) || closeMs > nowMs) return { kind: 'running', nz, utc };
  return { kind: 'passed', nz, utc };
}

interface Props {
  /** EVERY ballot the hub read — this panel does its own filtering, so the hub has
   *  one list and no second belief about which ballots are open. */
  ballots: DraftBallot[];
  /** The hub's real roll phase. It cannot change this panel's answer — `extend` does
   *  not consult it, because a ballot cannot reach 'open' on an unfrozen roll — and it
   *  is passed anyway rather than fabricating a `true` at the call: a literal invented
   *  to satisfy a shared rules function is a claim about the sitting that nothing
   *  checked. */
  rollFrozen: boolean;
  busy: boolean;
  onExtend: (ballot: DraftBallot) => void;
}

export function OpenPollsPanel({ ballots, rollFrozen, busy, onExtend }: Props): JSX.Element | null {
  // `permittedBallotActs` decides membership, not a state literal written here — a
  // second copy of "which word means running" is how the hollow-dot defect this panel
  // also fixes happened, on this same screen, in this same folder.
  const open = ballots.filter((ballot) => permittedBallotActs(ballot.state, rollFrozen).extend);
  // ABSENT ENTIRELY, not an empty panel — see the header.
  if (open.length === 0) return null;

  // ONE TICK FOR THE WHOLE PANEL, read once per render. Two rows judged against two
  // different `now`s could disagree about whether the same instant has passed.
  const nowMs = Date.now();

  return (
    <section className="setup-panel card" aria-labelledby="setup-open-polls-heading">
      <h2 className="setup-panel-heading" id="setup-open-polls-heading">
        Open now
      </h2>
      <p className="setup-panel-note">
        Members can mark a paper in these polls right now. If Synod needs longer than the closing
        time allows, move it later — that only ever gives people more time to vote, never less.
      </p>

      <ul className="setup-ballot-list">
        {open.map((ballot) => (
          <OpenPollRow
            key={ballot.id}
            ballot={ballot}
            poll={pollWindow(ballot, nowMs)}
            busy={busy}
            onExtend={() => onExtend(ballot)}
          />
        ))}
      </ul>
    </section>
  );
}

function OpenPollRow({
  ballot,
  poll,
  busy,
  onExtend,
}: {
  ballot: DraftBallot;
  /** Which of the three variants this row is — decided ONCE, by the panel, against one
   *  tick of the clock, so the rendering and the control cannot disagree about it. */
  poll: PollWindow;
  busy: boolean;
  onExtend: () => void;
}): JSX.Element {
  const kind = KIND_LABEL[ballot.itemKind] ?? ballot.itemKind;
  const stateWord = STATE_LABEL[ballot.state] ?? ballot.state;
  const passed = poll.kind === 'passed';

  return (
    <li className={`setup-ballot setup-poll${passed ? ' setup-poll-passed' : ''}`}>
      <div className="setup-ballot-head">
        <h3 className="setup-ballot-title">{ballot.title}</h3>
        <p className="setup-ballot-meta">
          <span className="setup-ballot-kind">{kind}</span>
          {/* The dot is FILLED for a running poll — the fix for the render that gave a
              binding poll the same hollow dot as an unbuilt draft. The word beside it
              carries the state again; colour carries nothing. */}
          <span className="setup-ballot-statelabel">
            <span
              className={`ballot-dot${stateDotIsHollow(ballot.state) ? ' ballot-dot-hollow' : ''}`}
              aria-hidden="true"
            />
            {stateWord}
          </span>
        </p>
      </div>

      {/* THE WINDOW, IN THE SHAPE EVERY OTHER SETUP SCREEN STATES IT: the registrar's
          own wall clock in ink, the worker's derived instant muted beneath. That
          pairing is the only place a human can see which daylight-saving offset was
          applied, and it matters here more than anywhere — Synod sits eight days before
          New Zealand moves to daylight saving, so a poll set by the clock is exactly
          one hour from being silently wrong. */}
      {poll.kind === 'unbounded' ? (
        <RecordedBound
          label="Closes"
          nz={null}
          utc={null}
          unbounded={
            poll.reported
              ? 'not on a clock — this poll stays open until a Scrutineer closes it. There is no closing time here to move.'
              : 'not reported by this server, so there is no closing time on this screen to move.'
          }
        />
      ) : poll.nz === null ? (
        // Bounded, but the wall clock was never recorded beside the instant (a window
        // set before migration 0015). The instant is stated on its own rather than
        // dressed up as a wall clock this screen would have to derive — which would be
        // the one arithmetic every file on this path forbids.
        <p className="setup-panel-loaded">
          <strong>Closes:</strong> {utcInWords(poll.utc)}.{' '}
          <span className="setup-field-hint">
            No New Zealand clock time was recorded alongside it. Setting a new closing time records
            both.
          </span>
        </p>
      ) : (
        <RecordedBound
          label="Closes"
          nz={poll.nz}
          utc={poll.utc}
          // Unreachable — `poll.nz` is narrowed to a string on this branch. Written as
          // a true sentence rather than an empty string, so it cannot become a blank
          // line if the narrowing above is ever loosened.
          unbounded="not on a clock — this poll stays open until a Scrutineer closes it."
        />
      )}

      {/* THE CONSEQUENCE, SAID PLAINLY. A closing time in the past is not an error
          message — it is a poll nobody in the hall can vote in, and the operator needs
          to be told that in the words it will be reported to them in. */}
      {passed ? (
        <p className="setup-panel-note">
          That time has passed. Members cannot mark a paper until it is moved later.
        </p>
      ) : null}

      {/* A DISCRETE GHOST, never a primary: this is one act on one row, and the panel's
          subject is the polls themselves. 48px through .setup-extend, the same floor
          every other act on this screen holds. The UNBOUNDED row has no control — see
          the header. */}
      {poll.kind === 'unbounded' ? null : (
        <div className="setup-ballot-actions">
          <button
            type="button"
            className="btn btn-ghost setup-extend"
            disabled={busy}
            onClick={onExtend}
          >
            {passed ? 'Reopen the time on this poll' : 'Give this poll more time'}
          </button>
        </div>
      )}
    </li>
  );
}
